import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { LargeTextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/LargeTextValue';
import { Message } from 'elements/minute/sidebar/sidebarComponents/atomic/Message';
import { SliderValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SliderValue';
import { ColorPalette } from 'elements/minute/sidebar/sidebarComponents/complex/ColorPalette';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSectionRow } from 'elements/minute/sidebar/sidebarSection/SidebarSectionRow';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import {
    recalculateCoordinatesForNewText,
    rotateRectangleCoordinates
} from 'hooks/coordinates/useCoordinatesComputations';
import { useMapheimDebounced } from 'hooks/general/useMapheimDebounced';
import { useIconPreview } from 'hooks/marker/useIconPreview';
import { EMPTY_IMAGE_URL } from 'hooks/marker/v2/useCreateWorldMarkerV2';
import { useContext } from 'react';
import { ITextV2WorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: ITextV2WorldMarker;
}

const WIDTH = [40, 60, 80, 100, 140, 180, 220];
const MAX_TEXT_LENGTH = 1000;

export const TextMarkerTextSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const directMarkerUpdates = useDirectMarkerUpdates();

    const iconPreview = useIconPreview({
        mapRef: ctxEdit.mapRef,
        worldMarkers: ctxEdit.worldMarkers,
        markerIds: [props.marker.id],
        markerType: 'image',
        onNewImageGenerated: ({ imageUrl, imageSize, isInitialLoad }) => {
            if (isInitialLoad && props.marker.elementData.imageUrl !== EMPTY_IMAGE_URL) {
                return;
            }
            const map = ctxEdit.mapRef?.current;
            const newCoordinates = recalculateCoordinatesForNewText(
                map,
                props.marker.elementData.nonRotatedCoordinates,
                imageSize,
                imageSize.width !== props.marker.elementData.lineLength ? 'width' : 'height'
            );
            const newRotatedCoordinates = rotateRectangleCoordinates(
                map,
                newCoordinates,
                props.marker.elementData.rotation
            );
            directMarkerUpdates.updateImage(props.marker.id, {
                imageUrl,
                coordinates: newRotatedCoordinates,
                nonRotatedCoordinates: newCoordinates
            });

            ctxEdit.updateWorldMarkerData(props.marker.id, {
                imageUrl,
                coordinates: newRotatedCoordinates,
                nonRotatedCoordinates: newCoordinates,
                lineLength: imageSize.width
            });
        },
        initialData: props.marker.elementData
    });

    function updateLabel(value: string) {
        iconPreview.setText(value);
        ctxEdit.updateWorldMarkerData(props.marker.id, { generatedText: value });
    }

    function updateColor(value: string) {
        iconPreview.setTextColor(value);
        ctxEdit.updateWorldMarkerData(props.marker.id, { generatedTextColor: value });
    }

    function updateWidth(value: number) {
        iconPreview.setWidth(value);
        ctxEdit.updateWorldMarkerData(props.marker.id, { generatedWidth: value });
    }

    function updateBackground(color: string) {
        iconPreview.setBackgroundColor(color);
        ctxEdit.updateWorldMarkerData(props.marker.id, {
            generatedBackgroundColor: color
        });
    }

    const updateLabelDebounced = useMapheimDebounced(updateLabel, 100);
    const updateWidthDebounced = useMapheimDebounced(updateWidth, 100);

    return (
        <S_TextMarkerLabelSection className={props.className}>
            <SidebarSection title="Text" icon={{ iconName: 'font' }} disableBottomBorder>
                <SidebarContent withTopPadding>
                    {props.marker.elementData.generatedText?.length >= MAX_TEXT_LENGTH && (
                        <S_WarningMessage type="warning">
                            You reached maximum of {MAX_TEXT_LENGTH} characters.
                        </S_WarningMessage>
                    )}
                    <LargeTextValue
                        value={props.marker.elementData.generatedText}
                        autoFocus
                        autoSelectAll={props.marker.elementData.imageUrl === EMPTY_IMAGE_URL}
                        maxLength={MAX_TEXT_LENGTH}
                        onChange={updateLabelDebounced}
                    />
                </SidebarContent>
                <SidebarContent withTopPadding>
                    <SidebarSectionRow>
                        <SliderValue
                            label={'Width'}
                            steps={WIDTH}
                            value={props.marker.elementData.generatedWidth}
                            onValueChange={updateWidthDebounced}
                        />
                    </SidebarSectionRow>
                </SidebarContent>
                <SidebarSubsection title={Localization.COLOR} icon={{ iconName: 'circle' }}>
                    <SidebarContent>
                        <ColorPalette
                            value={props.marker.elementData.generatedTextColor}
                            pickerAllowed
                            additionalColors={['#000000', '#ffffff']}
                            onChange={updateColor}
                        />
                    </SidebarContent>
                </SidebarSubsection>
                <SidebarSubsection title={Localization.BACKGROUND} icon={{ iconName: 'circle' }}>
                    <SidebarContent>
                        <ColorPalette
                            value={props.marker.elementData.generatedBackgroundColor}
                            pickerAllowed
                            transparentAllowed
                            onChange={updateBackground}
                        />
                    </SidebarContent>
                </SidebarSubsection>
            </SidebarSection>
            <S_Canvas ref={iconPreview.canvasRef} width={1000} height={1000} />
        </S_TextMarkerLabelSection>
    );
};

const S_TextMarkerLabelSection = styled.div``;

const S_WarningMessage = styled(Message)`
    margin-bottom: 10px;
`;

const S_Canvas = styled.canvas`
    width: 100px;
    height: 100px;
    display: none;
`;
